import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';

import { Loader } from 'common/components/Loader';
import { Button } from 'common/components/Button';
import {
    ICourseListItemFragment,
    LtiDeepLinkCoursesDocument,
} from 'graphql/types';
import { ContentContainer } from 'common/components/ContentContainer';
import { CourseAddList } from 'training/components/CourseAddList';
import { FilterBar } from 'common/components/FilterBar';
import { PageTitle } from 'common/components/PageTitle';

interface IProps {
    launchId: string;
    loadingDeeplink?: boolean;
    onSelectCourses(selectedCourses: ICourseListItemFragment[]): void;
}

export const LtiDeepLinkCourses = ({
    launchId,
    loadingDeeplink,
    onSelectCourses,
}: IProps) => {
    const [translate] = useTranslation();
    const [selectedCourses, setSelectedCourses] = useState<
        ICourseListItemFragment[]
    >([]);
    const [searchValue, setSearchValue] = useState('');

    const { data, loading } = useQuery(LtiDeepLinkCoursesDocument, {
        variables: { launchId },
    });

    // Simple frontend search — the backend already filters by LTI platform config
    const courses = useMemo(() => {
        const allCourses = data?.ltiDeepLinkCourses ?? [];

        if (!searchValue) return allCourses;

        const query = searchValue.toLowerCase();

        return allCourses.filter((course) =>
            course.title.toLowerCase().includes(query)
        );
    }, [data?.ltiDeepLinkCourses, searchValue]);

    const handleSelectCourse = (course: ICourseListItemFragment) => {
        const newCourses = [...selectedCourses, course];

        setSelectedCourses(newCourses);
    };

    const handleRemoveCourse = (course: ICourseListItemFragment) => {
        const newSelectedCourses = selectedCourses.filter(
            (selectedCourse) => selectedCourse.id !== course.id
        );

        setSelectedCourses(newSelectedCourses);
    };

    const handleSearch = (value: string) => {
        setSearchValue(value);
    };

    return (
        <>
            <PageTitle>
                {translate('lTIDeeplinkPage.coursesPageTitle')}
            </PageTitle>
            <Box sx={{ pb: 22.5, pt: 4 }}>
                <FilterBar
                    initialSearchValue={searchValue}
                    placeholder={translate('filterBarPlaceholder')}
                    onSearch={handleSearch}
                    onSearchClear={() => handleSearch('')}
                />

                {loading && <Loader />}

                {!loading && (
                    <CourseAddList
                        addedCourses={selectedCourses}
                        courses={courses}
                        selectedCourses={selectedCourses}
                        onAddCourse={handleSelectCourse}
                        onRemoveCourse={(
                            selectedCourse: ICourseListItemFragment
                        ) => handleRemoveCourse(selectedCourse)}
                    />
                )}
            </Box>

            <Box
                sx={{
                    position: 'fixed',
                    left: 0,
                    bottom: 0,
                    width: '100%',
                    backgroundColor: 'grey.100',
                    p: 2,
                }}
            >
                <ContentContainer maxWidth="md" sx={{ pb: 0 }}>
                    <Button
                        color="primary"
                        disabled={!selectedCourses.length}
                        loading={loadingDeeplink}
                        sx={{ width: '100%' }}
                        variant="contained"
                        onClick={() => {
                            onSelectCourses(selectedCourses);
                            setSelectedCourses([]);
                            setSearchValue('');
                        }}
                    >
                        {!selectedCourses.length
                            ? translate('addCoursesTitle')
                            : translate('addCourses', {
                                  count: selectedCourses.length,
                              })}
                    </Button>
                </ContentContainer>
            </Box>
        </>
    );
};
